import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfileRequest, resetProfileState } from '../store/whatsappProfileSlice';
import { MAX_LENGTHS, VALIDATION_RULES, FILE_CONSTRAINTS } from '../constants/profileConstants';

export const useProfileForm = (initialData = {}, isOpen) => {
  const dispatch = useDispatch();
  const { loading: isSubmitting, error: submitError, success } = useSelector(state => state.whatsappProfile);

  const defaultFormState = {
    displayName: '',
    displayNamePending: null,
    category: '',
    description: '',
    address: '',
    email: '',
    website1: '',
    website2: '',
    profileImage: null,
    profileImagePreview: null,
  };

  const [formData, setFormData] = useState(defaultFormState);
  const [initialFormData, setInitialFormData] = useState(defaultFormState);
  const [errors, setErrors] = useState({});

  const mapInitialDataToFormState = useCallback((profileData = {}) => ({
    displayName: profileData.displayName ?? '',
    displayNamePending: profileData.displayNamePending ?? null,
    category: profileData.category ?? '',
    description: profileData.description ?? '',
    address: profileData.address ?? '',
    email: profileData.email ?? '',
    website1: profileData.website1 ?? '',
    website2: profileData.website2 ?? '',
    profileImage: null,
    profileImagePreview: profileData.profileImage ?? null,
  }), []);

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      const newFormData = mapInitialDataToFormState(initialData);
      setFormData(newFormData);
      setInitialFormData(newFormData);
      setErrors({});
      dispatch(resetProfileState());
    }
  }, [isOpen, initialData, dispatch, mapInitialDataToFormState]);

  // Handle submit error from Redux
  useEffect(() => {
    if (submitError) {
      setErrors({ submit: submitError });
    }
  }, [submitError]);

  // When profile update is successful, sync the form state with new values
  useEffect(() => {
    if (success && isOpen && initialData) {
      const newFormData = mapInitialDataToFormState(initialData);
      setInitialFormData(newFormData);
      setFormData(newFormData);

      dispatch(resetProfileState());
    }
  }, [success, initialData, isOpen, dispatch, mapInitialDataToFormState]);

  const updateField = useCallback((name, value) => {
    // Prevent updating displayName if it's pending
    if (name === 'displayName' && formData.displayNamePending) {
      return;
    }

    // Validate length limits (skip for File objects)
    if (MAX_LENGTHS[name] && typeof value === 'string' && value.length > MAX_LENGTHS[name]) {
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field-specific error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors, formData.displayNamePending]);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'displayName':
        if (!value.trim()) return 'Display name is required';
        break;
      case 'email':
        if (value && !VALIDATION_RULES.email.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'website1':
      case 'website2':
        if (value && !VALIDATION_RULES.website.test(value)) {
          return 'Website URLs must start with http:// or https://';
        }
        break;
      default:
        break;
    }
    return null;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate required fields (skip displayName if pending)
    if (!formData.displayNamePending) {
      const displayNameError = validateField('displayName', formData.displayName);
      if (displayNameError) newErrors.displayName = displayNameError;
    }

    // Validate optional fields
    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const website1Error = validateField('website1', formData.website1);
    if (website1Error) newErrors.website1 = website1Error;

    const website2Error = validateField('website2', formData.website2);
    if (website2Error) newErrors.website2 = website2Error;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  const handleProfileImageChange = useCallback((file) => {
    if (!file) return null;

    // Validate file type
    if (!FILE_CONSTRAINTS.allowedTypes.includes(file.type)) {
      return 'Only PNG and JPEG images are allowed';
    }

    // Validate file size
    if (file.size > FILE_CONSTRAINTS.maxSize) {
      return 'File size must be less than 5MB';
    }

    // Store the File object for upload and create preview URL
    const previewUrl = URL.createObjectURL(file);
    updateField('profileImage', file);
    setFormData(prev => ({ ...prev, profileImagePreview: previewUrl }));
    return Promise.resolve();
  }, [updateField]);

  const removeProfileImage = useCallback(() => {
    updateField('profileImage', null);
    setFormData(prev => ({ ...prev, profileImagePreview: null }));
  }, [updateField]);

  // Get only modified fields compared to initial data
  const getModifiedFields = useCallback(() => {
    const modified = {};
    
    // Check each field for changes (exclude displayNamePending from change detection)
    const fieldsToCheck = ['displayName', 'category', 'description', 'address', 'email', 'website1', 'website2', 'profileImage'];
    
    fieldsToCheck.forEach(field => {
      if (field === 'profileImage') {
        // If a new image was selected, include it
        if (formData.profileImage && formData.profileImage !== initialFormData.profileImage) {
          modified.profileImageFile = formData.profileImage;
        }
        // If image was removed, also include that as null
        if (!formData.profileImage && initialFormData.profileImage) {
          modified.profileImageFile = null;
        }
      } else if (field === 'displayName') {
        // Don't include displayName if it's pending
        if (!formData.displayNamePending && formData[field] !== initialFormData[field]) {
          modified[field] = formData[field];
        }
      } else if (field === 'website1' || field === 'website2') {
        // Skip individual website fields - handle them separately in the websites array
      } else {
        // For all other fields, include if changed - including when cleared to empty string
        const isChanged = formData[field] !== initialFormData[field];
        if (isChanged) {
          modified[field] = formData[field];
        }
      }
    });

    // Handle websites array - send actual values or empty array if both are cleared
    const hasWebsiteChanges = 
      (formData.website1 !== initialFormData.website1) || 
      (formData.website2 !== initialFormData.website2);
    
    if (hasWebsiteChanges) {
      // Include non-empty websites or empty array if both cleared
      modified.websites = [formData.website1, formData.website2].filter(w => w && w.trim());
    }

    return modified;
  }, [formData, initialFormData]);

  const submitForm = useCallback(async (companyId, phoneNumberId) => {
    if (!validateForm()) return false;

    try {
      // Get only modified fields
      const modifiedFields = getModifiedFields();

      // Build payload with only required fields and modified fields
      const payload = {
        companyId,
        phoneNumberId,
        ...modifiedFields  // Only add fields that were actually changed
      };

      dispatch(updateProfileRequest(payload));
      return true;
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to save profile' });
      return false;
    }
  }, [formData, initialFormData, validateForm, dispatch, getModifiedFields]);

  return {
    formData,
    errors,
    isSubmitting,
    success,
    updateField,
    validateForm,
    handleProfileImageChange,
    removeProfileImage,
    submitForm,
  };
};